import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { Modal, Button } from "react-bootstrap";
import { useApi } from "../../../service/ApiContext";
import InterviewHubApiService from "../service/InterviewHubApiService";
import {
  InterviewSession,
  SessionStatus,
  InterviewRound,
} from "../data/interface/InterviewHubInterface";
import ResumePicker, { ResumeFileData } from "../components/ResumePicker";
import Loader from "../../../utils/Utils";
import axios from "axios";
import "../styles/InterviewHubStyles.css";
import InterviewSessionPage from "./InterviewSessionPage";
import ResumeGradingTab from "./ResumeGradingTab";
import ResumeAnalyzingTab from "./ResumeAnalyzingTab";
import { useAlert } from "../../../components/AlertContextComponent";
import InterviewProgressIcon from "../../../assets/images/interviewIQ/interviewinprogress.png";
import InterviewCompletedIcon from "../../../assets/images/interviewIQ/interviewcompleted.png";
import StartsAt from "../../../assets/images/calender/sand-clock-starts.png";
import EndsAt from "../../../assets/images/calender/sand-clock-ends.png";
import InterviewProgressUnSelectedIcon from "../../../assets/images/interviewIQ/interviewinprogressunselected.png";
import InterviewCompletedSelectedIcon from "../../../assets/images/interviewIQ/interviewcompletedselected.png";

type HubTab = "interview" | "grade" | "analyze";

const StatusTooltip: React.FC<{ message: string; isFailed?: boolean }> = ({
  message,
  isFailed,
}) => (
  <div
    className={`interview-hub-tooltip ${isFailed ? "interview-hub-tooltip-failed" : ""}`}
  >
    {message}
  </div>
);

const formatDateTime = (dateString: string) => {
  if (!dateString) return "";
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return dateString;
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  let hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12;
  const strHours = String(hours).padStart(2, "0");
  return `${day}/${month}/${year}, ${strHours}:${minutes} ${ampm}`;
};

const InterviewHubPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isChatRoute = location.pathname.endsWith("/chat");

  const api = useApi();
  const service = useMemo(() => new InterviewHubApiService(api), [api]);

  const [activeTab, setActiveTab] = useState<HubTab>("interview");
  const [sessions, setSessions] = useState<InterviewSession[]>([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadStep, setUploadStep] = useState<1 | 2>(1);
  const [selectedFile, setSelectedFile] = useState<ResumeFileData | null>(null);
  const [rounds, setRounds] = useState<InterviewRound[]>([]);
  const [selectedRoundId, setSelectedRoundId] = useState<string>("");
  const [loadingRounds, setLoadingRounds] = useState(false);
  const [visibleTooltip, setVisibleTooltip] = useState<string | null>(null);
  const [showTabs, setShowTabs] = useState(true);
  const { showAlert } = useAlert();
  const [interviewFilter, setInterviewFilter] = useState<
    "all" | "progress" | "completed"
  >("progress");

  const selectedRound = useMemo(
    () => rounds.find((r) => r.round_id === selectedRoundId),
    [rounds, selectedRoundId],
  );

  // State for Resume Grading Tab button
  const [showGradeUploadModal, setShowGradeUploadModal] = useState(false);
  // State for optimize
  const [showOptimizeModal, setShowOptimizeModal] = useState(false);
  useEffect(() => {
    if (!isChatRoute) {
      fetchSessions();
    }
  }, [isChatRoute]);

  if (isChatRoute) {
    const state = location.state as {
      sessionId?: string;
      resume?: boolean;
    } | null;
    const sessionId = state?.sessionId;
    const isResume = state?.resume || false;
    if (!sessionId) {
      return <Navigate to="/dashboard/interview-hub" replace />;
    }
    return <InterviewSessionPage id={sessionId} isResume={isResume} />;
  }

  const fetchSessions = async () => {
    Loader.showLoading("Fetching sessions...");
    try {
      const data = await service.listSessions();
      setSessions(data.sessions || []);
    } catch (error: any) {
      console.error("Failed to fetch sessions", error);
    } finally {
      Loader.dismissLoading();
    }
  };

  const fetchRounds = async () => {
    setLoadingRounds(true);
    try {
      const data = await service.listRounds();
      setRounds(data.rounds || []);
      if (data.rounds && data.rounds.length > 0) {
        setSelectedRoundId(data.rounds[0].round_id);
      }
    } catch (error) {
      console.error("Failed to fetch rounds", error);
    } finally {
      setLoadingRounds(false);
    }
  };

  const handleUploadAndStart = async () => {
    if (!selectedFile) return;
    Loader.showLoading("Uploading resume...");
    try {
      const uploadUrlData = await api.get<{
        uploadURL: string;
        key: string;
        downloadURL: string;
      }>(
        `/create_upload_url?filename=${encodeURIComponent(selectedFile.name)}`,
      );
      const { uploadURL, downloadURL } = uploadUrlData.data;
      await axios.put(uploadURL, selectedFile.binary, {
        headers: { "Content-Type": selectedFile.type },
      });
      const startResponse = await service.startInterview({
        file_url: downloadURL,
        round_id: selectedRoundId || undefined,
      });
      setShowUploadModal(false);
      if (startResponse.error) {
        showAlert({
          show: true,
          title: "Error",
          message: startResponse.error,
          alertType: "error",
          onlyNeedOneButton: true,
          button1Text: "Cancel",
          btn1onPressed() { },
        });
        return true;
      }
      setUploadStep(1);
      navigate(`/dashboard/interview-hub/chat`, {
        state: { sessionId: startResponse.session_id },
      });
    } catch (error: any) {
      console.error("Failed to start interview", error);
      const data = error?.response?.data;
      const msg =
        data?.error ||
        data?.detail ||
        "Something went wrong while starting the interview. Please try again.";
      showAlert({
        show: true,
        title: "Error",
        message: msg,
        alertType: "error",
        onlyNeedOneButton: true,
        button1Text: "Got it",
        btn1onPressed() { },
      });
    } finally {
      Loader.dismissLoading();
    }
  };

  const getStatusClass = (session: InterviewSession) => {
    switch (session.status) {
      case SessionStatus.IN_PROGRESS:
        return "interview-hub-status-inprogress";
      case SessionStatus.FAILED:
        return "interview-hub-status-failed";
      case SessionStatus.COMPLETED:
        return "interview-hub-status-completed";
      case SessionStatus.SCORED:
        return "interview-hub-status-scored";
      case SessionStatus.PENDING:
        return "interview-hub-status-pending";
      default:
        return session.is_completed
          ? "interview-hub-status-completed"
          : "interview-hub-status-inprogress";
    }
  };

  const getStatusLabel = (session: InterviewSession) => {
    switch (session.status) {
      case SessionStatus.IN_PROGRESS:
        return "In Progress";
      case SessionStatus.FAILED:
        return "Failed";
      case SessionStatus.COMPLETED:
        return "Completed";
      case SessionStatus.SCORED:
        return "Scored ✨";
      case SessionStatus.PENDING:
        return "Pending";
      default:
        return session.is_completed ? "Completed" : "In Progress";
    }
  };

  const getStatusIcon = (session: InterviewSession) => {
    switch (session.status) {
      case SessionStatus.IN_PROGRESS:
        return "🔄";
      case SessionStatus.FAILED:
        return "❌";
      case SessionStatus.COMPLETED:
        return "⏳";
      case SessionStatus.SCORED:
        return "🏆";
      case SessionStatus.PENDING:
        return "🕐";
      default:
        return "";
    }
  };

  const handleCardAction = (session: InterviewSession) => {
    switch (session.status) {
      case SessionStatus.IN_PROGRESS:
        navigate(`/dashboard/interview-hub/chat`, {
          state: { sessionId: session.session_id, resume: true },
        });
        break;
      case SessionStatus.SCORED:
        navigate(`/dashboard/interview-hub/feedback`, {
          state: { sessionId: session.session_id },
        });
        break;
      case SessionStatus.FAILED:
      case SessionStatus.COMPLETED:
        setVisibleTooltip((prev) =>
          prev === session.session_id ? null : session.session_id,
        );
        break;
      default:
        navigate(`/dashboard/interview-hub/chat`, {
          state: { sessionId: session.session_id, resume: true },
        });
    }
  };

  const getActionLabel = (session: InterviewSession) => {
    switch (session.status) {
      case SessionStatus.IN_PROGRESS:
        return "Resume";
      case SessionStatus.SCORED:
        return "View Results";
      case SessionStatus.FAILED:
        return "View Reason";
      case SessionStatus.COMPLETED:
        return "Status";
      default:
        return "Resume";
    }
  };

  const getTooltipMessage = (session: InterviewSession) => {
    if (session.status === SessionStatus.FAILED) {
      return session.reason || "Interview failed. Please try again.";
    }
    if (session.status === SessionStatus.COMPLETED) {
      return "We're processing your interview. Your results will be shared soon!";
    }
    return "";
  };

  return (
    <div className="interview-hub-container">
      {showTabs && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "2rem",
          }}
        >
          <div className="interview-hub-tabs animate-fade-in">
            <button
              className={`interview-hub-tab ${activeTab === "interview" ? "interview-hub-tab-active" : ""}`}
              onClick={() => setActiveTab("interview")}
            >
              Interview
            </button>
            <button
              className={`interview-hub-tab ${activeTab === "grade" ? "interview-hub-tab-active" : ""}`}
              onClick={() => setActiveTab("grade")}
            >
              Resume Grade
            </button>
            <button
              className={`interview-hub-tab ${activeTab === "analyze" ? "interview-hub-tab-active" : ""}`}
              onClick={() => setActiveTab("analyze")}
            >
              Match & Optimize
            </button>
          </div>

          <div style={{ display: "flex", gap: "1rem", height: "fit-content" }}>
            {/* Filter icons - only for interview tab */}
            {activeTab === "interview" && (
              <div
                style={{
                  display: "flex",
                  border: "1px solid #DDDDDD",
                  background: "#F6F6F6",
                  borderRadius: "12px",
                  padding: "0.2rem",
                  height: "fit-content",
                }}
              >
                <img
                  src={
                    interviewFilter === "progress"
                      ? InterviewProgressIcon
                      : InterviewProgressUnSelectedIcon
                  }
                  alt=""
                  style={{
                    cursor: "pointer",
                    width: "40px",
                    height: "30px",
                  }}
                  onClick={() =>
                    setInterviewFilter(
                      interviewFilter === "progress" ? "all" : "progress",
                    )
                  }
                />
                <img
                  src={
                    interviewFilter === "completed"
                      ? InterviewCompletedSelectedIcon
                      : InterviewCompletedIcon
                  }
                  alt=""
                  style={{
                    cursor: "pointer",
                    width: "40px",
                    height: "30px",
                  }}
                  onClick={() =>
                    setInterviewFilter(
                      interviewFilter === "completed" ? "all" : "completed",
                    )
                  }
                />
              </div>
            )}

            {/* Action Button - changes based on active tab */}
            <div className="interview-hub-header animate-fade-in">
              {activeTab === "interview" && (
                <button
                  className="interview-hub-start-btn"
                  onClick={() => {
                    setShowUploadModal(true);
                    setUploadStep(1);
                    fetchRounds();
                  }}
                >
                  + Start New Interview
                </button>
              )}
              {activeTab === "grade" && (
                <button
                  className="interview-hub-start-btn"
                  onClick={() => setShowGradeUploadModal(true)}
                >
                  Grade My Resume
                </button>
              )}
              {activeTab === "analyze" && (
                <button
                  className="interview-hub-start-btn"
                  onClick={() => setShowOptimizeModal(true)}
                >
                  Analyze Job Match
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {/* ── Interview Tab */}
      {activeTab === "interview" && (
        <>
          {(() => {
            let filteredSessions = sessions;
            if (interviewFilter === "progress") {
              filteredSessions = sessions.filter(
                (s) => s.status === SessionStatus.IN_PROGRESS,
              );
            } else if (interviewFilter === "completed") {
              filteredSessions = sessions.filter(
                (s) =>
                  s.status === SessionStatus.SCORED ||
                  s.status === SessionStatus.COMPLETED,
              );
            }

            if (filteredSessions.length > 0) {
              return (
                <div className="interview-hub-sessions-grid">
                  {filteredSessions.map((session) => {
                    const isInProgress =
                      session.status === SessionStatus.IN_PROGRESS;
                    const isScored = session.status === SessionStatus.SCORED;
                    const isFailed = session.status === SessionStatus.FAILED;
                    const isCompleted =
                      session.status === SessionStatus.COMPLETED;
                    const showTip = visibleTooltip === session.session_id;
                    const tipMsg = getTooltipMessage(session);

                    return (
                      <div
                        key={session.session_id}
                        className="interview-hub-session-card"
                      >
                        <div className="interview-hub-card-content">
                          <div className="interview-hub-round-type">
                            {session.round_type
                              ? session.round_type.toLowerCase() === "hr" ||
                                session.round_type.toLowerCase() === "hm"
                                ? session.round_type.toUpperCase() +
                                " Interview"
                                : session.round_type.charAt(0).toUpperCase() +
                                session.round_type.slice(1) +
                                " Interview"
                              : "Technical Interview"}
                          </div>

                          {isInProgress && !session.submitted_at && (
                            <div className="interview-hub-session-date">
                              <img
                                src={StartsAt}
                                alt="calendar"
                                style={{ height: "14px", width: "14px" }}
                              />
                              {formatDateTime(session.created_at)}
                            </div>
                          )}

                          {(isScored || isCompleted) &&
                            session.submitted_at && (
                              <div className="interview-hub-dates">
                                <div className="interview-hub-session-date">
                                  <img
                                    src={StartsAt}
                                    alt="start"
                                    style={{ height: "14px", width: "14px" }}
                                  />
                                  {formatDateTime(session.created_at)}
                                </div>
                                <div className="interview-hub-session-date">
                                  <img
                                    src={EndsAt}
                                    alt="submitted"
                                    style={{ height: "14px", width: "14px" }}
                                  />
                                  {formatDateTime(session.submitted_at)}
                                </div>
                              </div>
                            )}


                          <span
                            className={`interview-hub-status ${getStatusClass(session)}`}
                          >
                            {getStatusIcon(session)} {getStatusLabel(session)}
                          </span>

                          {isInProgress && session.current_phase && (
                            <div className="interview-hub-session-phase">
                              Phase: {session.current_phase}
                            </div>
                          )}
                        </div>

                        <div className="interview-hub-button-wrapper" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "8px" }}>
                          {isScored && typeof session.score === "number" && (
                            <div className="interview-hub-session-score" style={{ marginTop: 0 }}>
                              <span className="interview-hub-score-value" style={{ fontSize: "18px", fontWeight: "bold" }}>
                                {session.score}/10
                              </span>
                            </div>
                          )}
                          <div className="interview-hub-tooltip-wrapper">
                            {showTip && tipMsg && (
                              <StatusTooltip
                                message={tipMsg}
                                isFailed={
                                  session.status === SessionStatus.FAILED
                                }
                              />
                            )}
                            <button
                              className={`interview-hub-action-btn ${isCompleted || isFailed
                                  ? "interview-hub-action-btn-muted"
                                  : ""
                                }`}
                              onClick={() => handleCardAction(session)}
                              disabled={isCompleted}
                            >
                              {getActionLabel(session)}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            } else {
              return (
                <div className="interview-hub-empty-state">
                  <h3>
                    {interviewFilter === "progress"
                      ? "No In Progress Interviews"
                      : "No Completed Interviews"}
                  </h3>
                  <p style={{ textAlign: "center" }}>
                    {interviewFilter === "progress"
                      ? "Start a new interview to see it here."
                      : "Complete an interview to see results here."}
                  </p>
                </div>
              );
            }
          })()}
        </>
      )}
      {/* ── Resume Grading Tab ── */}
      {activeTab === "grade" && (
        <ResumeGradingTab
          onToggleTabs={setShowTabs}
          showUploadModal={showGradeUploadModal}
          setShowUploadModal={setShowGradeUploadModal}
        />
      )}
      {activeTab === "analyze" && (
        <ResumeAnalyzingTab
          onToggleTabs={setShowTabs}
          showInitModal={showOptimizeModal}
          setShowInitModal={setShowOptimizeModal}
        />
      )}
      {/* ── Upload Modal for Interview - Single Step */}
      <Modal
        show={showUploadModal}
        onHide={() => {
          setShowUploadModal(false);
          setSelectedFile(null);
          setSelectedRoundId("");
        }}
        centered
        contentClassName="interview-hub-upload-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title
            style={{ color: "#1f2937", fontWeight: 600, fontSize: "1.25rem" }}
          >
            Start New Interview
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-4">
            {/* Description text */}
            <p
              style={{
                color: "#6b7280",
                fontSize: "0.875rem",
                marginBottom: "1.5rem",
                lineHeight: 1.5,
              }}
            >
              Choose the round you want to practice. Attach a resume if you want
              the questions tailored to your background.
            </p>

            {/* Select Interview Round */}
            <label
              className="form-label"
              style={{
                fontWeight: 600,
                color: "#374151",
                marginBottom: "0.5rem",
                display: "block",
                fontSize: "0.875rem",
              }}
            >
              Select Interview Round <span style={{ color: "#ef4444" }}>*</span>
            </label>

            {loadingRounds ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  padding: "2rem",
                }}
              >
                <div
                  className="interview-hub-feedback-spinner"
                  style={{ width: 30, height: 30, borderWidth: 3 }}
                />
              </div>
            ) : rounds.length > 0 ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.75rem",
                }}
              >
                <div style={{ position: "relative" }}>
                  <select
                    className="form-control"
                    value={selectedRoundId}
                    onChange={(e) => setSelectedRoundId(e.target.value)}
                    style={{
                      backgroundColor: "#ffffff",
                      color: "#1f2937",
                      border: "1px solid #d1d5db",
                      borderRadius: "8px",
                      padding: "0.75rem",
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      cursor: "pointer",
                      appearance: "none",
                      width: "100%",
                    }}
                  >
                    {rounds.map((r) => (
                      <option
                        key={r.round_id}
                        value={r.round_id}
                        style={{
                          backgroundColor: "#ffffff",
                          color: "#1f2937",
                        }}
                      >
                        {r.name}
                      </option>
                    ))}
                  </select>
                  <div
                    style={{
                      position: "absolute",
                      right: "0.75rem",
                      top: "50%",
                      transform: "translateY(-50%)",
                      pointerEvents: "none",
                      color: "#6b7280",
                    }}
                  >
                    ▼
                  </div>
                </div>

                {selectedRound && (
                  <div
                    style={{
                      background: "#fefce8",
                      borderLeft: "4px solid #fbbf24",
                      borderRadius: "8px",
                      padding: "0.75rem 1rem",
                    }}
                  >
                    <p
                      style={{
                        color: "#92400e",
                        fontSize: "0.875rem",
                        margin: 0,
                        lineHeight: 1.5,
                      }}
                    >
                      {selectedRound.description ||
                        "No description available for this round."}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <p
                style={{
                  color: "#6b7280",
                  fontSize: "0.875rem",
                  padding: "1rem",
                  background: "#f9fafb",
                  borderRadius: "8px",
                  textAlign: "center",
                }}
              >
                No interview rounds available right now. Please try again later.
              </p>
            )}

            {/* Upload Resume  */}
            <div className="mt-4">
              <label
                className="form-label"
                style={{
                  fontWeight: 600,
                  color: "#374151",
                  marginBottom: "0.5rem",
                  display: "block",
                  fontSize: "0.875rem",
                }}
              >
                Upload Resume <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <ResumePicker file={selectedFile} onChange={setSelectedFile} />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            style={{
              background: "transparent",
              border: "1px solid #d1d5db",
              borderRadius: "8px",
              padding: "0.5rem 1rem",
              color: "#6b7280",
              cursor: "pointer",
              fontWeight: 500,
              fontSize: "0.875rem",
            }}
            onClick={() => {
              setShowUploadModal(false);
              setSelectedFile(null);
              setSelectedRoundId("");
            }}
          >
            Cancel
          </button>
          <button
            className="interview-hub-start-btn"
            style={{
              background: "#fbbf24",
              border: "none",
              borderRadius: "8px",
              padding: "0.5rem 1.25rem",
              color: "#1a1a2e",
              fontWeight: 600,
              cursor: "pointer",
              fontSize: "0.875rem",
            }}
            disabled={loadingRounds || rounds.length === 0 || !selectedRoundId}
            onClick={handleUploadAndStart}
          >
            Start Interview
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default InterviewHubPage;
